/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-this-in-sfc */
/* eslint-disable func-names */
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import _ from 'lodash';
import lineDistance from '@turf/length';

const DrawCircle = _.extend({}, MapboxDraw.modes.draw_polygon);

function createVertex(parentId, coordinates, path, selected) {
    return {
        type: 'Feature',
        properties: {
            meta: 'vertex',
            parent: parentId,
            coord_path: path,
            active: selected ? 'true' : 'false'
        },
        geometry: {
            type: 'LineString',
            coordinates
        }
    };
}

function createGeoJSONCircle(line, points = 64) {
    const geojson = line.toGeoJSON();
    const centre = geojson.geometry.coordinates?.[0]?.[0];
    if (!geojson && !centre) return {};
    const radiusInKm = lineDistance(geojson, { units: 'kilometers' }) / 2;

    const coords = {
        latitude: centre[1],
        longitude: centre[0]
    };

    const km = radiusInKm;

    const ret = [];
    const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    const distanceY = km / 110.574;

    let theta;
    let x;
    let y;
    for (let i = 0; i < points; i += 1) {
        theta = (i / points) * (2 * Math.PI);
        x = distanceX * Math.cos(theta);
        y = distanceY * Math.sin(theta);

        ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]);

    return {
        type: 'Feature',
        geometry: {
            type: 'Polygon',
            coordinates: [ret]
        },
        properties: {
            parent: line.id,
            active: 'true',
            radius: radiusInKm * 1000,
            centre: centre
        }
    };
}

const doubleClickZoom = {
    enable: ctx => {
        setTimeout(() => {
            // First check we've got a map and some context.
            if (
                !ctx.map ||
                !ctx.map.doubleClickZoom ||
                !ctx._ctx ||
                !ctx._ctx.store ||
                !ctx._ctx.store.getInitialConfigValue
            ) {
                return;
            }
            // Now check initial state wasn't false (we leave it disabled if so)
            if (!ctx._ctx.store.getInitialConfigValue('doubleClickZoom')) return;
            ctx.map.doubleClickZoom.enable();
        }, 0);
    }
};

DrawCircle.onSetup = function (opts) {
    let result = MapboxDraw.modes.draw_polygon.onSetup.call(this, null);

    if (opts.edit) {
        const feature = this.getFeature(opts.edit);
        const c = feature.coordinates[0];
        const midPoint = c.length / 2;
        result.polygon.updateCoordinate(
            '0.' + result.currentVertexPosition++,
            // average of two opposite coords == centre
            (c[0][0] + c[midPoint][0]) / 2,
            (c[0][1] + c[midPoint][1]) / 2
        );
        result.polygon.updateCoordinate(
            '0.' + result.currentVertexPosition,
            opts.lngLat.lng,
            opts.lngLat.lat
        );
        result.circleHeight = feature.properties.height;

        const point = [opts?.lngLat?.lng, opts?.lngLat?.lat];
        if (feature?.properties?.centre && point) {
            this.changeMode(constants.modes.DRAW_LINE_STRING);
            // Simulate adding two coordinates
            this.addFeature(
                this.newFeature({
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: [feature?.properties?.centre, point]
                    }
                })
            );
            this.addFeature(
                this.newFeature({
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        type: 'LineString',
                        coordinates: [feature?.properties?.centre]
                    }
                })
            );

            // Finish drawing
            this.changeMode(constants.modes.SIMPLE_SELECT);
        }

        this.deleteFeature(opts.edit, { silent: true });
        this.map.dragPan.disable();
    }

    return result;
};

DrawCircle.clickAnywhere = function (state, e) {
    if (state.currentVertexPosition > 0) {
        return this.changeMode(MapboxDraw.constants.modes.SIMPLE_SELECT, {
            featureIds: [state.polygon.id]
        });
    }

    return MapboxDraw.modes.draw_polygon.clickAnywhere.call(this, state, e);
};

DrawCircle.onMouseMove = DrawCircle.onDrag = function (state, e) {
    MapboxDraw.modes.draw_polygon.onMouseMove.call(this, state, e);
};

DrawCircle.onTouchEnd = DrawCircle.onMouseUp = function (state, e) {
    DrawCircle.clickAnywhere.call(this, state, e);
};

DrawCircle.onStop = function (state) {
    doubleClickZoom.enable(this);
    this.activateUIButton();

    if (this.getFeature(state.polygon.id) === undefined) {
        this.map.fire('draw.cancelled');
        return;
    }

    const coordinates = state.polygon?.coordinates?.[0];
    if (coordinates?.length > 1) {
        if (_.isEqual(coordinates[0], coordinates[1])) {
            this.deleteFeature(state.polygon.id, { silent: true });
            return;
        }
        const circleGeoJSON = createGeoJSONCircle(state.polygon);
        if (state.circleHeight) {
            circleGeoJSON.properties.height = state.circleHeight;
        }
        const id = state.polygon.id;
        this.deleteFeature(id, { silent: true });
        delete circleGeoJSON.properties.parent;
        const circleFeature = this.newFeature(circleGeoJSON);
        circleFeature.id = id;
        this.addFeature(circleFeature);
        this.map.fire('draw.create', {
            features: [circleGeoJSON]
        });
    } else {
        this.deleteFeature(state.polygon.id, { silent: true });
        this.changeMode('simple_select', {}, { silent: true });
    }
};

DrawCircle.toDisplayFeatures = function (state, geojson, display) {
    if (geojson.properties.id === state.polygon.id) {
        geojson.properties.active = 'true';
        // Only render the line if it has at least one real coordinate
        if (geojson.geometry.coordinates?.[0].length < 2) return;
        geojson.properties.meta = 'feature';

        display(geojson);
        const circleGeoJSON = createGeoJSONCircle(state.polygon);
        if (state.circleHeight) {
            // we need the 'user_' prefix for display features
            circleGeoJSON.properties.user_height = state.circleHeight;
        }

        display(circleGeoJSON);
        this.map.fire('draw.editing', {
            features: [circleGeoJSON]
        });
    } else {
        geojson.properties.active = 'false';
        display(geojson);
    }
};

export default DrawCircle;
